import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { AchEnrollmentStatus } from '@ocx/graphql';
import { logInDev } from '@ocx/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';

import { useSpireConfig } from './hooks/use-spire-config.hook';
import { useSpirePostMessage } from './hooks/use-spire-post-message.hook';
import { SpireComponent } from './spire.component';
import { SpireLayout } from './spire.layout';
import { SpireEvent } from './spire.types';
import { ACH_STATUS_SNACKBAR_CONFIG } from './spire.constants';
import { isSupportedAchStatus } from './spire.utils';
import { spireMessages } from './spire.messages';

const SpireController = () => {
  const spireConfig = useSpireConfig();
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const snackbar = useSnackbar();
  const intl = useIntl();

  const handleIframeLoad = useCallback(() => {
    logInDev('[Spire] Iframe loaded');
    setIsIframeLoading(false);
  }, []);

  const handleEvent = useCallback((event: SpireEvent) => {
    logInDev(`[Spire] Event received: ${event}`);
  }, []);

  const handleStatusUpdateSuccess = useCallback(
    (status: AchEnrollmentStatus | null) => {
      if (!isSupportedAchStatus(status)) {
        return;
      }

      const config = ACH_STATUS_SNACKBAR_CONFIG[status];
      snackbar.open(intl.formatMessage(spireMessages[config.messageKey]), {
        variant: config.variant,
      });
    },
    [snackbar, intl],
  );

  const handleStatusUpdateError = useCallback(() => {
    snackbar.open(intl.formatMessage(spireMessages['spire:status-update-failed']), {
      variant: 'error',
    });
  }, [snackbar, intl]);

  // Listen for postMessage events from Spire iframe (only if enabled)
  useSpirePostMessage({
    iframeUrl: spireConfig.enabled ? spireConfig.iframeUrl : '',
    skip: !spireConfig.enabled,
    onEvent: handleEvent,
    onStatusUpdateSuccess: handleStatusUpdateSuccess,
    onStatusUpdateError: handleStatusUpdateError,
  });

  // This should never happen due to ConfigGuard, but handle gracefully
  if (!spireConfig.enabled) {
    logInDev('[Spire] Feature not enabled');
    return null;
  }

  return (
    <SpireLayout isLoading={isIframeLoading}>
      <SpireComponent iframeUrl={spireConfig.iframeUrl} onLoad={handleIframeLoad} />
    </SpireLayout>
  );
};

export default SpireController;
